import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import { formatUSD as rawFormatUSD, formatNumber as rawFormatNumber } from '@/lib/sovereign';

const STORAGE_KEY = 'origin-privacy-mode';
const MASK = '\u2022\u2022\u2022\u2022';

interface PrivacyContextValue {
  isHidden: boolean;
  toggle: () => void;
  formatUSD: (value: number, decimals?: number) => string;
  formatNumber: (value: number, decimals?: number) => string;
  mask: (value: string) => string;
}

const PrivacyContext = createContext<PrivacyContextValue>({
  isHidden: false,
  toggle: () => {},
  formatUSD: rawFormatUSD,
  formatNumber: rawFormatNumber,
  mask: (v) => v,
});

export function PrivacyProvider({ children }: { children: ReactNode }) {
  const [isHidden, setIsHidden] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, String(isHidden));
    } catch {}
  }, [isHidden]);

  const toggle = useCallback(() => setIsHidden((prev) => !prev), []);

  const formatUSD = useCallback(
    (value: number, decimals?: number) => (isHidden ? `$${MASK}` : rawFormatUSD(value, decimals)),
    [isHidden]
  );

  const formatNumber = useCallback(
    (value: number, decimals?: number) => (isHidden ? MASK : rawFormatNumber(value, decimals)),
    [isHidden]
  );

  const mask = useCallback((value: string) => (isHidden ? MASK : value), [isHidden]);

  return (
    <PrivacyContext.Provider value={{ isHidden, toggle, formatUSD, formatNumber, mask }}>
      {children}
    </PrivacyContext.Provider>
  );
}

export function usePrivacyMode() {
  return useContext(PrivacyContext);
}
